<template>
    <v-container fluid class="article py-0 px-0 page-shell">
        <v-row no-gutters class="my-0 py-0">
            <v-col cols="12" class="text-center secondary-font px-0 py-0">
                <v-card class="article-hero-card">
                    <div
                        class="article-hero-watermark"
                        :style="{ backgroundImage: `url('${getImage()}')` }"
                    />
                    <v-img
                        :src="getImage()"
                        class="article-hero-image"
                        contain
                        position="center center"
                    />
                    <div class="article-hero-caption custom-orange">
                        <v-card-title class="text-h6 white--text article-title" v-text="article ? article.title : ''" />
                        <div class="article-hero-share">
                            <v-card-subtitle class="white--text article-date"> {{ getDate() }} </v-card-subtitle>
                            <v-menu v-if="article" v-model="shareMenuOpen" top left offset-y :nudge-top="4">
                                <template #activator="{ on, attrs }">
                                    <v-btn
                                        icon
                                        small
                                        class="white--text article-share-btn"
                                        aria-label="Κοινοποίηση άρθρου"
                                        v-bind="attrs"
                                        v-on="on"
                                    >
                                        <v-icon small>mdi-share-variant</v-icon>
                                    </v-btn>
                                </template>
                                <v-list dense class="article-share-list">
                                    <v-list-item
                                        v-for="target in shareTargets"
                                        :key="target.key"
                                        @click="target.action"
                                    >
                                        <v-list-item-icon class="mr-3">
                                            <v-icon small>{{ target.icon }}</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-title>{{ target.label }}</v-list-item-title>
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                        </div>
                    </div>
                </v-card>
            </v-col>
        </v-row>
        <v-row class="white article-body-row" no-gutters justify="center" align="center">
            <v-col cols="12" align="center" class="py-10 article-body">
                <div class="article-body-content" v-html="article ? article.body : ''" />
            </v-col>
        </v-row>
        <v-row v-if="galleryImages.length" class="white article-gallery-row" no-gutters justify="center" align="center">
            <v-col cols="12" class="article-gallery-wrap px-2 py-2">
                <h3 class="article-gallery-title">Gallery</h3>
                <v-row>
                    <v-col
                        v-for="(image, index) in galleryImages"
                        :key="image.id || `gallery-${index}`"
                        cols="12"
                        sm="6"
                        md="4"
                        class="article-gallery-col"
                    >
                        <v-card outlined class="article-gallery-card">
                            <v-img
                                :src="getGalleryImage(image)"
                                :alt="image.description || `Gallery image ${index + 1}`"
                                class="article-gallery-image"
                                contain
                                @click="openGalleryPreview(image, index)"
                            />
                        </v-card>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>

        <v-dialog v-model="isGalleryPreviewOpen" max-width="1100" content-class="article-preview-dialog" @click:outside="closeGalleryPreview">
            <v-card class="article-preview-card">
                <div class="article-preview-toolbar">
                    <v-spacer />
                    <v-btn icon @click="closeGalleryPreview">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </div>
                <v-img
                    :src="previewImageSrc"
                    :alt="previewImageAlt"
                    class="article-preview-image"
                    contain
                />
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
    import articleService from '@/services/article'
    export default {
        data() {
            return {
                article: null,
                isGalleryPreviewOpen: false,
                previewImageSrc: '',
                previewImageAlt: '',
                shareMenuOpen: false,
                linkCopied: false
            }
        },
        computed: {
            galleryImages() {
                const gallery = this.article && Array.isArray(this.article.imageGallery) ? this.article.imageGallery : []
                return gallery.filter(image => image && image.imageName)
            },
            shareUrl() {
                return typeof window !== 'undefined' ? window.location.href : ''
            },
            shareText() {
                if (this.article && this.article.title) {
                    return this.article.title
                }
                return typeof document !== 'undefined' ? document.title : ''
            },
            shareTargets() {
                const url = this.shareUrl
                const text = this.shareText
                const encodedUrl = encodeURIComponent(url)
                const encodedText = encodeURIComponent(text)
                const targets = []

                if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
                    targets.push({ key: 'native', label: 'Κοινοποίηση…', icon: 'mdi-share-variant', action: () => this.nativeShare() })
                }

                targets.push(
                    { key: 'facebook', label: 'Facebook', icon: 'mdi-facebook', action: () => this.openShareWindow(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`) },
                    { key: 'twitter', label: 'X / Twitter', icon: 'mdi-twitter', action: () => this.openShareWindow(`https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`) },
                    { key: 'whatsapp', label: 'WhatsApp', icon: 'mdi-whatsapp', action: () => this.openShareWindow(`https://api.whatsapp.com/send?text=${encodeURIComponent(text + ' ' + url)}`) },
                    { key: 'telegram', label: 'Telegram', icon: 'mdi-telegram', action: () => this.openShareWindow(`https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`) },
                    { key: 'email', label: 'Email', icon: 'mdi-email-outline', action: () => { window.location.href = `mailto:?subject=${encodedText}&body=${encodedUrl}` } },
                    { key: 'copy', label: this.linkCopied ? 'Ο σύνδεσμος αντιγράφηκε' : 'Αντιγραφή συνδέσμου', icon: 'mdi-link-variant', action: () => this.copyLink() }
                )

                return targets
            }
        },
        watch: {
            '$route.params.id': {
                handler() {
                    this.getArticle()
                }
            }
        },
        created() {
            this.getArticle()
        },
        methods: {
            getImage() {
                if (this.article && this.article.image) {
                    return `${process.env.VUE_APP_GOOGLE_BUCKET_URL}${this.article.image.imageName}`
                } else {
                    return require('@/assets/main_logo.png')
                }
            },
            getDate() {
                if (!this.article || !this.article.articleDate) {
                    return ''
                }

                const date = new Date(this.article.articleDate)
                return date.getDate() + '/' + (date.getMonth() + 1) + '/' + date.getFullYear()
            },
            getGalleryImage(image) {
                if (!image || !image.imageName) {
                    return require('@/assets/main_logo.png')
                }

                return `${process.env.VUE_APP_GOOGLE_BUCKET_URL}${image.imageName}`
            },
            openGalleryPreview(image, index) {
                const src = this.getGalleryImage(image)
                if (!src) {
                    return
                }

                this.previewImageSrc = src
                this.previewImageAlt = (image && image.description) ? image.description : `Gallery image ${index + 1}`
                this.isGalleryPreviewOpen = true
            },
            closeGalleryPreview() {
                this.isGalleryPreviewOpen = false
            },
            openShareWindow(link) {
                window.open(link, '_blank', 'noopener,noreferrer')
            },
            nativeShare() {
                if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
                    navigator.share({ title: this.shareText, url: this.shareUrl }).catch(() => {})
                }
            },
            copyLink() {
                const url = this.shareUrl
                const markCopied = () => {
                    this.linkCopied = true
                    setTimeout(() => { this.linkCopied = false }, 2000)
                }

                if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
                    navigator.clipboard.writeText(url).then(markCopied).catch(() => this.fallbackCopy(url, markCopied))
                } else {
                    this.fallbackCopy(url, markCopied)
                }
            },
            fallbackCopy(text, onSuccess) {
                const textarea = document.createElement('textarea')
                textarea.value = text
                textarea.setAttribute('readonly', '')
                textarea.style.position = 'absolute'
                textarea.style.left = '-9999px'
                document.body.appendChild(textarea)
                textarea.select()
                try {
                    document.execCommand('copy')
                    onSuccess()
                } catch (error) {
                    // ignore copy failure
                }
                document.body.removeChild(textarea)
            },
            getArticle() {
                articleService.getArticle(this.$route.params.id).then(resp => {
                    this.article = resp.data.article
                    this.$nextTick(() => {
                        this.syncTinyMceContentMedia()
                    })
                })
            },
            syncTinyMceContentMedia() {
                const contentRoot = this.$el && this.$el.querySelector
                    ? this.$el.querySelector('.article-body-content')
                    : null

                if (!contentRoot) {
                    return
                }

                const images = contentRoot.querySelectorAll('img')
                images.forEach((image) => {
                    image.style.maxWidth = '100%'
                    image.style.height = image.style.height || 'auto'
                })

                const mediaNodes = contentRoot.querySelectorAll('iframe, video')
                mediaNodes.forEach((node) => {
                    const widthAttr = Number(node.getAttribute('width'))
                    const heightAttr = Number(node.getAttribute('height'))
                    const hasValidRatio = widthAttr > 0 && heightAttr > 0

                    node.style.maxWidth = '100%'
                    node.style.display = node.style.display || 'block'
                    node.style.border = node.style.border || '0'
                    node.style.marginLeft = node.style.marginLeft || 'auto'
                    node.style.marginRight = node.style.marginRight || 'auto'

                    if (hasValidRatio) {
                        if (!node.style.width) {
                            node.style.width = `min(100%, ${widthAttr}px)`
                        }

                        node.style.height = 'auto'
                        node.style.aspectRatio = `${widthAttr} / ${heightAttr}`
                    } else {
                        if (!node.style.width) {
                            node.style.width = '100%'
                        }

                        node.style.height = node.style.height || 'auto'
                    }
                })
            }
        }
    }
</script>

<style scoped>
.article,
.article * {
    text-transform: none !important;
}

.article {
    width: 100%;
}

.article-hero-card {
    position: relative;
    isolation: isolate;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    background-color: #0f2740;
}

.article-hero-watermark {
    position: absolute;
    inset: 0;
    background-size: cover;
    background-position: center;
    opacity: 0.3;
    filter: blur(8px);
    transform: scale(1.06);
    z-index: 0;
    pointer-events: none;
}

.article-hero-image {
    position: relative;
    z-index: 1;
    width: 100%;
    height: clamp(260px, 48vw, 620px);
    background-color: transparent;
}

.article-hero-caption {
    position: relative;
    z-index: 1;
    padding: 8px 10px;
}

.article-title {
    text-align: justify;
    word-break: break-word;
    line-height: 1.3;
    padding: 4px 8px;
}

.article-date {
    text-align: end;
    word-break: break-word;
    padding: 0 8px 4px;
}

.article-hero-share {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 2px;
}

.article-hero-share .article-date {
    padding-bottom: 0;
}

.article-share-btn {
    margin-bottom: 2px;
}

.article-share-list .v-list-item {
    cursor: pointer;
}

.article-body-row {
    margin-top: 16px;
}

.article-gallery-row {
    margin-top: 6px;
    margin-bottom: 14px;
}

.article-gallery-card {
    border-radius: 10px;
    margin: 18px;
    background: #fff;
    overflow: hidden;
    box-sizing: border-box;
}

.article-gallery-title {
    margin: 0 0 10px;
    text-align: left;
    color: #223152;
    text-transform: none !important;
}

.article-gallery-image {
    width: 100%;
    max-width: 100%;
    height: clamp(180px, 26vw, 300px);
    border-radius: 8px;
    background: #f8fafc;
    cursor: zoom-in;
}

.article-gallery-image::v-deep .v-image__image,
.article-gallery-image::v-deep .v-image__placeholder {
    max-width: 100%;
}

.article-preview-card {
    background: #fff;
    border-radius: 10px;
    overflow: hidden;
}

.article-preview-toolbar {
    display: flex;
    align-items: center;
    padding: 6px 8px;
}

.article-preview-image {
    width: 100%;
    height: min(80vh, 820px);
    background: #f8fafc;
}

.article-body {
    font-size: clamp(1rem, 1.2vw, 1.125rem);
    overflow-x: hidden;
    padding-left: 26px;
    padding-right: 26px;
}

.article-body-content {
    display: block;
    width: 100%;
    max-width: 100%;
    font-family: Ubuntu, Arial, sans-serif !important;
    text-align: left;
    word-break: break-word;
    padding: 0px 10px 0px 10px;
}

.article-body::v-deep img {
    max-width: 100%;
    height: auto;
}

.article-body::v-deep video,
.article-body::v-deep iframe {
    max-width: 100%;
    display: block;
    margin-left: auto;
    margin-right: auto;
}

.article-body::v-deep figure {
    max-width: 100%;
    margin-left: auto;
    margin-right: auto;
    text-align: center;
}

.article-body::v-deep table {
    display: block;
    width: 100%;
    overflow-x: auto;
}

@media only screen and (max-width: 960px) {
    .article {
        padding-left: 8px;
        padding-right: 8px;
    }

    .article-body {
        padding-left: 12px;
        padding-right: 12px;
    }

    .article-gallery-wrap {
        padding-left: 12px;
        padding-right: 12px;
    }

    .article-gallery-col {
        padding: 6px;
    }
}

@media only screen and (max-width: 600px) {
    .article-body-row {
        margin-top: 8px;
    }

    .article-hero-image {
        height: clamp(220px, 62vw, 340px);
    }

    .article-title {
        font-size: 1rem;
    }

    .article-body {
        font-size: 1rem;
        padding-left: 8px;
        padding-right: 8px;
    }

    .article-gallery-wrap {
        padding-left: 8px;
        padding-right: 8px;
    }

    .article-gallery-col {
        padding: 4px;
    }
}
</style>

<style>
.article-body-content,
.article-body-content * {
    text-transform: none !important;
}
</style>
